import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {combineLatest} from 'rxjs';
import {RowEntry, UserErrors} from '../models';
import {MiniBookAdminService} from '../service/mini-book-admin.service';
import {AdditionalFieldData} from '../shared/AdditionalFieldData';
import {Attendee} from '../shared/Attendee';
import {GeneralConfig} from '../shared/GeneralConfig';

@Component({
    selector: 'edu-udo-itmc-dev-mini-book-attendee',
    templateUrl: './attendee.component.html',
    styleUrls: ['./attendee.component.css'],
    standalone: false
})
export class AttendeeComponent implements OnInit {

  sentinalNullValue = AdditionalFieldData.SENTINAL_NULL_VALUE;

  attendee!: Attendee;

  generalConfig!: GeneralConfig;

  timetableEntries: RowEntry[] = [];
  errors?: UserErrors;

  additionalFieldData: {[id: string]: AdditionalFieldData} = {};

  constructor(
    private adminService: MiniBookAdminService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('attendeeId')) {
      const id = +this.route.snapshot.paramMap.get('attendeeId')!;
      if (Number.isNaN(id) || id === 0) {
        this.router.navigate(['..'], {relativeTo: this.route});
      } else {
        combineLatest([this.adminService.attendees.get(id), this.adminService.generalConfig.getGeneralConfig()]).subscribe({
          next: ([attendee, generalConfig]) => {
            this.attendee = attendee;
            this.generalConfig = generalConfig;
            this.convertAdditionalFields(attendee);
          },
          error: err => console.log(err)
        });
        combineLatest([this.adminService.attendees.getTimetable(id), this.adminService.timeslots.getTimeslots(), this.adminService.rooms.getRooms()]).subscribe({
          next: ([timetable, timeslots, rooms]) => {
            this.timetableEntries = timetable.map(entry => {
              return {
                selected: false,
                entry: entry,
                timeslot: timeslots.find(ts => ts.id === entry.timeslotId)!,
                room: rooms.find(r => r.id === entry.roomId)!
              };
            });
          }
        });
      }
    }
  }

  save() {
    this.attendee.additionalFieldData = [];
    this.generalConfig.additionalFields.forEach(additionalField => {
      this.attendee.additionalFieldData.push(this.additionalFieldData[additionalField.id]);
    });
    this.adminService.attendees.update(this.attendee).subscribe({
      next: attendee => {
        this.attendee = attendee;
        this.convertAdditionalFields(attendee);
        this.errors = undefined;
        this.messageService.add({severity: 'success', summary: 'Gesichert', detail: 'Teilnehmerdaten wurden gespeichert'});
      },
      error: err => {
        if ('error' in err && typeof err.error == 'object' && 'type' in err.error && err.error.type === 'UserErrors') {
          this.errors = err.error;
        } else {
          const message: any = {
            type: "PlainErrorMessage",
            summary: "Fehler bei der Kommunikation mit dem Backend",
            detail: err.message
          };
          this.errors = {
            type: "UserErrors",
            messages: [message]
          };
        }
      }
    });
  }

  back() {
    history.back();
  }

  convertAdditionalFields(attendee: Attendee) {
    this.additionalFieldData = {};
    attendee.additionalFieldData.forEach(value => this.additionalFieldData[value.id] = value);
    this.generalConfig.additionalFields.forEach(additionalField => {
      if (!this.additionalFieldData[additionalField.id]) {
        this.additionalFieldData[additionalField.id] = new AdditionalFieldData(additionalField.id, additionalField.type);
      }
      if (additionalField.selectShowOther) {
        const index = this.additionalFieldData[additionalField.id].values.findIndex(value => !additionalField.selectableValues.includes(value));
        if (index != -1) {
          this.additionalFieldData[additionalField.id].selectOtherField = this.additionalFieldData[additionalField.id].values[index];
          this.additionalFieldData[additionalField.id].values[index] = this.sentinalNullValue;
        }
      }
      if (!this.additionalFieldData[additionalField.id].type) {
        this.additionalFieldData[additionalField.id].type = additionalField.type!;
        if (additionalField.type == 'STRING') {
          this.additionalFieldData[additionalField.id].values = this.additionalFieldData[additionalField.id].values.map(value => '' + value);
        }
      }
    });
  }

  openPDF(forceDownload: boolean) {
    const filename = 'timetable_'
      + this.attendee.firstname.replace('/', '_')
      + '_'
      + this.attendee.lastname.replace('/', '_')
      + '.pdf';
    if (forceDownload) {
      this.adminService.attendees.getTimetablePdfUrl(this.attendee.id).subscribe({
        next: pdfBlob => {
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const body = document.getElementsByTagName('body')[0];
          const element = <HTMLAnchorElement>document.createElement('A');
          element.href = pdfUrl;
          element.download = filename;
          body.appendChild(element);
          element.click();
          body.removeChild(element);
        }
      });
    }else{
      window.open(this.adminService.attendees.timetablePdfUrlWithToken(this.attendee.id, filename));
    }
  }
}
